import { useEffect, useState } from 'react'
import { MessageCircle, Send, ThumbsUp, Reply, Trash2, ChevronDown, ChevronUp, ShieldAlert, Clock } from 'lucide-react'
import { contentApi, type ContentComment } from '../lib/api'

interface CommentSectionProps {
  contentId: string
  isLoggedIn: boolean
}

interface CommentNode extends ContentComment {
  replies: ContentComment[]
}

const CommentSection = ({ contentId, isLoggedIn }: CommentSectionProps) => {
  const [items, setItems] = useState<CommentNode[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState('')
  const [replyTo, setReplyTo] = useState<ContentComment | null>(null)
  const [replyDraft, setReplyDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState('') // 提交成功提示（如"待审核"）
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)

  const load = async (p = 1, append = false) => {
    setLoading(true)
    setError('')
    try {
      const r = await contentApi.listComments(contentId, { page: p, page_size: 20 })
      const newOnes: CommentNode[] = (r.items || []).map((c: any) => ({
        ...c,
        replies: c.replies || [],
      }))
      setItems(prev => append ? [...prev, ...newOnes] : newOnes)
      setTotal(r.total || 0)
      setHasMore(p * 20 < (r.total || 0))
      setPage(p)
    } catch (e: any) {
      setError(e?.message || '加载失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setItems([])
    setTotal(0)
    setPage(1)
    setHasMore(false)
    load(1)
  }, [contentId])

  const onSubmit = async () => {
    if (!isLoggedIn) {
      alert('请先登录后参与评论')
      return
    }
    const body = draft.trim()
    if (!body) return
    if (body.length > 1000) {
      alert('评论内容不能超过 1000 字')
      return
    }
    setSubmitting(true)
    try {
      await contentApi.postComment(contentId, { body })
      setDraft('')
      setNotice('评论已提交，审核通过后将对所有人展示。你可以在下方查看自己的待审核评论。')
      load(1)
    } catch (e: any) {
      alert('发表失败：' + (e?.message || '网络错误'))
    } finally {
      setSubmitting(false)
    }
  }

  const onReply = async (parentId: string) => {
    if (!isLoggedIn) {
      alert('请先登录后回复')
      return
    }
    const body = replyDraft.trim()
    if (!body) return
    setSubmitting(true)
    try {
      await contentApi.postComment(contentId, { body, parent_id: parentId })
      setReplyTo(null)
      setReplyDraft('')
      setNotice('回复已提交，审核通过后将展示。')
      load(1)
    } catch (e: any) {
      alert('回复失败：' + (e?.message || '网络错误'))
    } finally {
      setSubmitting(false)
    }
  }

  const onLike = async (id: string) => {
    if (!isLoggedIn) {
      alert('请先登录后点赞')
      return
    }
    try {
      const res = await contentApi.likeComment(id)
      setItems(prev => prev.map(c => c.id === id ? { ...c, like_count: res.like_count } : c))
    } catch (e: any) {
      alert('点赞失败：' + (e?.message || '网络错误'))
    }
  }

  const onDelete = async (id: string) => {
    if (!confirm('确认删除该评论？')) return
    try {
      await contentApi.deleteOwnComment(id)
      load(1)
    } catch (e: any) {
      alert('删除失败：' + (e?.message || '网络错误'))
    }
  }

  const toggleCollapse = (id: string) => {
    setCollapsed(prev => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem('auth_user') || '{}').id || '' } catch { return '' }
  })()

  return (
    <div className="bg-white rounded-2xl shadow-md p-5">
      <h3 className="text-sm font-medium mb-3 flex items-center gap-1">
        <MessageCircle className="w-4 h-4" />用户评论 <span className="text-gray-400">({total})</span>
      </h3>

      {/* 评论输入框 */}
      <div className="bg-gray-50 rounded-xl p-3 mb-4">
        {!isLoggedIn && (
          <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" />请先登录后参与评论
          </div>
        )}
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          disabled={!isLoggedIn || submitting}
          rows={3}
          maxLength={1000}
          placeholder={isLoggedIn ? '说点什么吧…' : '登录后即可评论'}
          className="w-full bg-white border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:text-gray-400"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400">{draft.length} / 1000</span>
          <button
            onClick={onSubmit}
            disabled={!isLoggedIn || submitting || !draft.trim()}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-3 h-3" />发表
          </button>
        </div>
      </div>

      {/* 提交成功提示（待审核说明） */}
      {notice && (
        <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 mb-3">
          <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice('')} className="text-amber-400 hover:text-amber-600 ml-1">×</button>
        </div>
      )}

      {loading && items.length === 0 && (
        <div className="text-center text-sm text-gray-400 py-4">加载中…</div>
      )}
      {error && (
        <div className="text-center text-sm text-red-500 py-2">{error}</div>
      )}
      {!loading && items.length === 0 && (
        <div className="text-center text-sm text-gray-400 py-4">暂无评论，来发表第一条吧</div>
      )}

      <div className="space-y-3">
        {items.map(c => (
          <div key={c.id} className="border-b border-gray-100 pb-3 last:border-b-0">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 text-white flex items-center justify-center text-xs flex-shrink-0">
                {(c.user_name || c.user_phone || '匿').slice(-2)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="font-medium text-gray-700">{c.user_name || c.user_phone || '匿名用户'}</span>
                  {c.status === 'pending' && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 text-[10px]">
                      <Clock className="w-2.5 h-2.5" />待审核，仅自己可见
                    </span>
                  )}
                  <span>{c.created_at?.slice(0, 16).replace('T', ' ')}</span>
                </div>
                {c.status === 'hidden' ? (
                  <div className="text-sm text-gray-400 italic mt-1">[评论已隐藏]</div>
                ) : (
                  <div className="text-sm text-gray-800 mt-1 break-words whitespace-pre-wrap">{c.body}</div>
                )}
                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                  <button onClick={() => onLike(c.id)} className="flex items-center gap-0.5 hover:text-blue-600 transition-colors">
                    <ThumbsUp className="w-3 h-3" />{c.like_count || 0}
                  </button>
                  <button onClick={() => { setReplyTo(c); setReplyDraft('') }} className="flex items-center gap-0.5 hover:text-green-600 transition-colors">
                    <Reply className="w-3 h-3" />回复
                  </button>
                  {currentUserId === c.user_id && (
                    <button onClick={() => onDelete(c.id)} className="flex items-center gap-0.5 hover:text-red-600 transition-colors">
                      <Trash2 className="w-3 h-3" />删除
                    </button>
                  )}
                </div>

                {replyTo?.id === c.id && (
                  <div className="mt-2 bg-gray-50 rounded-lg p-2">
                    <div className="text-xs text-gray-500 mb-1">回复 {replyTo.user_name || replyTo.user_phone || '匿名用户'}：</div>
                    <textarea
                      value={replyDraft}
                      onChange={e => setReplyDraft(e.target.value)}
                      disabled={submitting}
                      rows={2}
                      maxLength={1000}
                      placeholder="说点什么…"
                      className="w-full bg-white border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                    <div className="flex justify-end gap-2 mt-1.5">
                      <button onClick={() => { setReplyTo(null); setReplyDraft('') }} className="px-2 py-1 text-xs text-gray-500">取消</button>
                      <button onClick={() => onReply(c.id)} disabled={submitting || !replyDraft.trim()} className="px-2 py-1 text-xs bg-green-600 text-white rounded disabled:opacity-40">发送</button>
                    </div>
                  </div>
                )}

                {/* 二级回复预览（最多显示 3 条） */}
                {c.replies && c.replies.length > 0 && (
                  <div className="mt-2">
                    {(collapsed.has(c.id) ? [] : c.replies.slice(0, 3)).map(r => (
                      <div key={r.id} className="bg-gray-50 rounded-lg p-2 mt-1.5">
                        <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                          <span className="font-medium text-gray-700">{r.user_name || r.user_phone || '匿名用户'}</span>
                          {r.status === 'pending' && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 text-[10px]">
                              <Clock className="w-2.5 h-2.5" />待审核
                            </span>
                          )}
                          <span>{r.created_at?.slice(0, 16).replace('T', ' ')}</span>
                        </div>
                        {r.status === 'hidden' ? (
                          <div className="text-sm text-gray-400 italic mt-1">[评论已隐藏]</div>
                        ) : (
                          <div className="text-sm text-gray-800 mt-1 break-words">{r.body}</div>
                        )}
                      </div>
                    ))}
                    {c.replies.length > 3 && (
                      <button onClick={() => toggleCollapse(c.id)} className="flex items-center gap-0.5 text-xs text-gray-500 hover:text-green-600 mt-1.5">
                        {collapsed.has(c.id) ? <><ChevronDown className="w-3 h-3" />展开全部 {c.replies.length} 条回复</> : <><ChevronUp className="w-3 h-3" />收起</>}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="text-center mt-3">
          <button
            onClick={() => load(page + 1, true)}
            disabled={loading}
            className="text-xs px-3 py-1.5 border rounded-full hover:bg-gray-50 disabled:opacity-40"
          >
            {loading ? '加载中…' : '加载更多评论'}
          </button>
        </div>
      )}
    </div>
  )
}

export default CommentSection
